<script lang="ts">
  import StatsCard from './StatsCard.svelte'
  import statsTailOne from '$lib/assets/branding/stats-tail-1.mp4'
  import LineSvg from '../../components/SVGs/LineSVG.svelte'
  import { onMount } from 'svelte'
  import { getProtocolSummary } from '$lib/protocol/protocolSummary'

  let data: {
    activeCdps: number | string
    allTimeCdps: number | string
    totalValueLocked: number | string
    totalDebt: number | string
  } = {
    activeCdps: '—',
    allTimeCdps: '—',
    totalValueLocked: '—',
    totalDebt: '—',
  }
  let loadError: string | null = null

  function formatCompactValue(value: number, dollarSign: boolean): string {
    if (value >= 1000) {
      const formatted = new Intl.NumberFormat('en', {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 1,
      }).format(value)
      return dollarSign ? `$${formatted}` : formatted
    }

    const rounded = Number(value.toFixed(1))
    return dollarSign ? `$${rounded}` : `${rounded}`
  }

  onMount(async () => {
    try {
      data = await fetchData()
    } catch (error) {
      loadError = error instanceof Error ? error.message : String(error)
      console.error('Failed to load homepage stats:', error)
    }
  })

  async function fetchData() {
    const dataRes = await getProtocolSummary()
    return {
      ...dataRes,
      totalDebt:
        dataRes.totalDebt == null
          ? '—'
          : formatCompactValue(Number(dataRes.totalDebt) / 1_000_000, true),
      totalValueLocked:
        dataRes.totalValueLocked == null
          ? '—'
          : formatCompactValue(Number(dataRes.totalValueLocked) / 1_000_000, true),
      activeCdps: formatCompactValue(Number(dataRes.activeCdps), false),
      allTimeCdps: formatCompactValue(Number(dataRes.allTimeCdps), false),
    }
  }
</script>

<div class="mx-auto max-w-[1700px]">
  <section class="sm-desktop:space-y-80">
    {#if loadError}
      <div class="mx-4 mb-6 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-muted-foreground">
        Failed to load homepage stats: {loadError}
      </div>
    {/if}
    <ul class="relative grid grid-cols-[repeat(auto-fit,minmax(350px,1fr))] sm-tablet:grid-cols-2">
      <StatsCard
        title="Outstanding Interest"
        dollarSign
        totalAmount={data.totalDebt}
        videoPath={statsTailOne}
        videoContainerStyles="left-[50%] top-[-50px] z-[0]"
        videoStyles="scale-110 max-h-[450px]"
        scrollerLabel="Total Debt Value"
      />

      <StatsCard title="TVL" dollarSign totalAmount={data.totalValueLocked} scrollerLabel="Total Value Locked" />
      <StatsCard title="Active CDPs" totalAmount={data.activeCdps} scrollerLabel="Active CDPs" />
      <StatsCard title="CDPs Created" totalAmount={data.allTimeCdps} scrollerLabel="CDPs Created" />
    </ul>

    <h2 class="mx-4 my-24 w-[85%] font-graphik text-[7vw] leading-[130%] sm-tablet:text-[45px] sm-desktop:ml-10">
      <LineSvg svgStyles="mr-2 inline w-[49px]" />
      Our mission is to deliver a truly ground-breaking approach to the realm of synthetic assets, designed to optimise liquidity
      and boast immense scalability.
    </h2>
  </section>
</div>

<style lang="postcss">
  @media (min-width: 600px) {
    h2 {
      font-size: 42px;
      width: 500px;
    }
  }

  @media (min-width: 700px) {
    ul {
      --divider-color: #272d37;
    }

    ul > :global(*) {
      border-right: 2px solid var(--divider-color);
    }

    ul > :global(*:nth-child(2n)) {
      border-right: none;
    }

    ul > :global(*:nth-last-child(-n + 2)) {
      border-bottom: none;
    }
  }
  @media (min-width: 765px) {
    h2 {
      width: 65vw;
    }
  }
  @media (min-width: 1440px) {
    h2 {
      line-height: 70.4px;
      width: 1180px;
      font-size: 64px;
    }
  }
</style>
